import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Plus, FolderOpenDot } from "lucide-react";

export default function WritingView() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data.projects || []);
      } catch (e) {
        toast.error("No se pudieron cargar tus proyectos");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-10 max-w-6xl mx-auto" data-testid="writing-view">
      <p className="text-[11px] tracking-[0.22em] uppercase text-copper font-medium">
        Asistente de redacción
      </p>
      <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ink leading-tight text-balance">
        Elige el proyecto en el que vas a escribir hoy.
      </h1>
      <p className="mt-3 text-[14px] text-ink-soft max-w-2xl">
        Abrir un proyecto te lleva al editor de capítulos con el copiloto al lado. Puedes pedirle ideas, validar la coherencia o reescribir un párrafo.
      </p>

      {loading ? (
        <p className="mt-10 text-ink-soft">Cargando…</p>
      ) : projects.length === 0 ? (
        <div className="mt-10 rounded-2xl border border-rule bg-white p-10 text-center">
          <p className="font-serif text-2xl text-ink">Aún no tienes proyectos.</p>
          <p className="mt-2 text-[13px] text-ink-soft">
            Crea uno desde "Mis Proyectos" y vuelve aquí.
          </p>
          <button
            data-testid="writing-create-project"
            onClick={() => navigate("/dashboard")}
            className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full text-[13px] font-medium text-paper bg-forest hover:bg-forest-dark"
          >
            <Plus size={14} /> Crear mi primer proyecto
          </button>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="writing-projects-grid">
          {projects.map((p) => (
            <button
              key={p.project_id}
              data-testid={`writing-open-${p.project_id}`}
              onClick={() => navigate(`/dashboard/projects/${p.project_id}`)}
              className="group text-left rounded-2xl border border-rule bg-white p-5 hover:shadow-[0_20px_40px_-25px_rgba(39,59,44,0.25)] transition-all flex flex-col gap-3"
            >
              <div className="flex items-center gap-2">
                <span className="grid place-items-center h-9 w-9 rounded-xl bg-forest/10 text-forest">
                  <FolderOpenDot size={15} />
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-paper-2 px-2.5 py-0.5 text-[10px] tracking-[0.18em] uppercase font-medium text-ink-soft">
                  {p.citation_format}
                </span>
              </div>
              <p className="font-serif text-[18px] text-ink leading-snug line-clamp-2">{p.title}</p>
              <p className="text-[12.5px] text-ink-soft">
                {p.career}
                {p.university ? ` · ${p.university}` : ""}
              </p>
              <div className="mt-1 h-1.5 rounded-full bg-paper-2 overflow-hidden">
                <div className="h-full bg-forest" style={{ width: `${p.progress_percent || 0}%` }} />
              </div>
              <span className="mt-auto inline-flex items-center gap-1.5 text-[12.5px] font-medium text-forest group-hover:text-forest-dark">
                <Sparkles size={12} />
                Abrir con el copiloto
                <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
